import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '../../../../general/inputs/Button';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { generateNote, generateNoteTitle, updateConsultationNote } from '../../../../../routes/doctor/consultation';
import { setDescription, setTitle, setUpdatedTitle, setShouldAutoGenerateNote, setIsLoadingTranscription } from '../../../../../redux/features/doctor/Dashboard/consultation/consultationNote';

const GenerateNote: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { consultationId, consultationNoteId } = useParams();

    const colors = useAppSelector(state => state.theme.colors);
    const securityPin = useAppSelector(state => state.consultation.securityPin);
    
    const noteType = useAppSelector(state => state.consultation.noteType);
    
    const mainAudioTranscription = useAppSelector(state => state.consultationNote.audioTranscription);
    const additionalStatementsTranscription = useAppSelector(state => state.consultationNote.additionalTranscription);
    const shouldAutoGenerate = useAppSelector(state => state.consultationNote.shouldAutoGenerateNote);

    const [isGenerating, setIsGenerating] = useState(false);

    const handleNoteGeneration = async () => {
        if (isGenerating) return;

        const currentMainText = mainAudioTranscription.trim();
        const currentAdditionalText = additionalStatementsTranscription.trim();

        const combinedText = currentAdditionalText
            ? `${currentMainText} ${currentAdditionalText}`.trim()
            : currentMainText;

        if (!combinedText) {
            console.warn("GenerateNote: No transcribed text available to generate note from.");
            if (shouldAutoGenerate) {
                dispatch(setShouldAutoGenerateNote(false));
                dispatch(setIsLoadingTranscription(false));
            }
            return;
        }
        
        setIsGenerating(true);
        console.log("GenerateNote: Generating note from text:", combinedText);

        try {
            // THE FIX IS HERE (2/2): Pass the noteType.id to the API call
            const res = await generateNote(consultationId, combinedText, noteType.id);

            if (res?.data.status === 200) {
                console.log("GenerateNote: Note generated successfully.");
                dispatch(setDescription(res.data.note));
                dispatch(setIsLoadingTranscription(false));

                const content = {
                    consultationNoteId: consultationNoteId,
                    securityPin: securityPin,
                    description: res.data.note
                };
                await updateConsultationNote(content);
                console.log("GenerateNote: Backend note description updated.");

                try {
                    const titleRes = await generateNoteTitle(consultationId!, res.data.note);
                    if (titleRes?.data?.status === 200) {
                        const noteTitle = titleRes.data.title;
                        dispatch(setTitle(noteTitle));
                        dispatch(setUpdatedTitle(noteTitle));
                        await updateConsultationNote({ consultationNoteId, securityPin, title: noteTitle });
                        console.log("GenerateNote: Title generated and saved.");
                    }
                } catch (titleErr) {
                    console.error("GenerateNote: Error generating or saving title:", titleErr);
                }

            } else {
                 console.error("GenerateNote: API call failed", res?.data);
                 throw new Error(res?.data?.error || 'Failed to generate note (API error)');
            }
        } catch (error: any) {
             console.error("GenerateNote: Error during note generation or update:", error);
        } finally {
            setIsGenerating(false);
            if (shouldAutoGenerate) {
                dispatch(setShouldAutoGenerateNote(false));
            }
        }
    };

    useEffect(() => {
        if (shouldAutoGenerate && !isGenerating) {
            const currentMainText = mainAudioTranscription.trim();
            const currentAdditionalText = additionalStatementsTranscription.trim();
            if (currentMainText || currentAdditionalText) {
                console.log("GenerateNote: Auto-triggering note generation.");
                handleNoteGeneration();
            } else {
                dispatch(setShouldAutoGenerateNote(false));
            }
        }
    }, [shouldAutoGenerate, isGenerating, mainAudioTranscription, additionalStatementsTranscription, dispatch]);


    const canGenerate = (mainAudioTranscription.trim().length > 0 || additionalStatementsTranscription.trim().length > 0);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: '95%',
                paddingTop: 2,
                paddingBottom: 2,
            }}>

            <Button
                title='generateNote'
                icon={<AutoAwesomeIcon sx={{ color: 'white', fontSize: 18 }} />}
                triggerFunction={handleNoteGeneration}
                isDisabled={isGenerating || !canGenerate}
            />

        </Box>
    );
};

export default GenerateNote;